import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, branch, renderComponent, withHandlers } from 'recompose';

import { withSparefootSelfStorageSchema } from 'decorators';

import { H3, Paragraph, Text } from 'components/core/Typography';
import { Link } from 'components/core/Link';
import { Button } from 'components/core/Button';
import { Image } from 'components/core/Image';
import Marker from 'components/svg/Marker';
import OnlineMoveIn from 'components/svg/OnlineMoveIn';
import { FlexColumn, FlexRow } from 'components/atoms/Flex';
import { FacilityImage } from 'components/facility/FacilityImage';
import ImageNotAvailable from 'components/facility/ImageNotAvailable/ImageNotAvailable';
import imageComingSoon from 'assets/images/facility/image-coming-soon.png';
import { Badge } from 'components/molecules/Badge';
import StarRating from 'components/molecules/StarRating/StarRating';
import { trackCTAClick } from 'components/core/library/tracking-utils/tracking/clickTrackingUtils';
import { GhostFacilityCard } from 'components/search/GhostFacilityCard';
import facilityCardDealIcon from 'assets/images/icons/facility-card-deal-icon.svg';
import trustedPatnerBadge from 'assets/images/icons/trusted-partner-badge.svg';
import './DealsFacilityCard.scss';

const getLowestPricedUnit = (units) => {
	if (!units || units.length === 0) return null;

	return units.reduce((lowest, unit) => {
		const currentPrice = parseFloat(unit.price?.text || '999999');
		const lowestPrice = parseFloat(lowest?.price?.text || '999999');
		return currentPrice < lowestPrice ? unit : lowest;
	}, units[0]);
};

export const enhance = compose(
	withHandlers({
		handleDeselect:
			({ handleFacilityDeselected, handlePinDeselected }) =>
			() => {
				if (handleFacilityDeselected) {
					handleFacilityDeselected();
				}
				if (handlePinDeselected) {
					handlePinDeselected();
				}
			},
		handleSelect:
			({ handleFacilitySelected, handlePinDeselected, facility }) =>
			() => {
				if (handleFacilitySelected && facility?.id) {
					handleFacilitySelected(facility.id);
				}
				if (handlePinDeselected) {
					handlePinDeselected();
				}
			},
		handleTrackCTA:
			({ facility, baseUrl }) =>
			() => {
				if (facility) {
					const facilityUrl = facility?.url?.facility
						? `${baseUrl}${facility.url.facility}`
						: undefined;
					trackCTAClick(
						'view-all-units',
						'View all units/prices Button',
						'Compare all units at this facility',
						facilityUrl,
					);
				}
			},
	}),
	withSparefootSelfStorageSchema,
	branch(({ loading }) => loading, renderComponent(GhostFacilityCard)),
);

export function DealsFacilityCard({
	facility,
	baseUrl,
	facilitySelected,
	handleDeselect,
	handleSelect,
	handleTrackCTA,
	lazyLoad,
	hasOMI,
	selfStorageSchema,
	withSchema,
	isHighlighted,
	isVipFacility,
	dealBannerText,
}) {
	const lowestUnit = getLowestPricedUnit(facility?.units);
	const displayPrice = lowestUnit?.price?.text;
	const originalPrice = lowestUnit?.regularPrice?.text;

	// Validate that prices are valid numbers
	const isValidPrice = (price) => price && !isNaN(parseFloat(price));
	const hasValidDisplayPrice = isValidPrice(displayPrice);
	const hasValidOriginalPrice = isValidPrice(originalPrice);

	const hasDiscount =
		lowestUnit &&
		hasValidDisplayPrice &&
		hasValidOriginalPrice &&
		parseFloat(originalPrice) > parseFloat(displayPrice);

	return (
		<FlexColumn
			className={classnames(
				'deals-facility-card',
				'facility-card-loaded',
				{
					selected: facility?.id === facilitySelected,
					highlighted: isHighlighted,
					'vip-facility': isVipFacility,
					'has-deal-banner': !!dealBannerText,
				},
			)}
			onMouseEnter={handleSelect}
			onMouseLeave={handleDeselect}
			itemScope
			itemType="https://schema.org/LocalBusiness"
		>
			{dealBannerText && (
				<div className="facility-card__deal-banner">
					<div className="facility-card__deal-content">
						<Image
							className="facility-card__deal-icon"
							src={facilityCardDealIcon}
							alt="Facility Card Deal Icon"
						/>
						<div className="facility-card__deal-text">
							{dealBannerText}
						</div>
					</div>
				</div>
			)}
			<FlexColumn
				gapSmall
				className="facility-head"
			>
				<Link
					href={`${baseUrl}${facility?.url?.facility ?? ''}`}
					hideBorder
					productClick
				>
					{facility?.images?.[0] ? (
						<FacilityImage
							itemProp="url"
							src={facility.images[0]}
							alt={`Photo of ${
								facility?.name ?? 'storage facility'
							}`}
							size="medium"
							lazyLoad={lazyLoad}
						/>
					) : (
						<ImageNotAvailable
							size="medium"
							defaultImage={imageComingSoon}
							imageText={null}
						/>
					)}
				</Link>
				{isVipFacility && (
					<div className="vip-facility__badge">
						<div className="vip-facility__badge-content">
							<Image
								className="trusted-partner-badge-img"
								src={trustedPatnerBadge}
								alt="Trusted Partner Badge"
							/>
							<div className="vip-facility__badge-text">
								<div className="vip-facility__badge-top-text">
									Trusted Partner Facility
								</div>
							</div>
						</div>
					</div>
				)}
			</FlexColumn>
			<FlexColumn
				className="facility-details"
				gapSmall
			>
				<FlexRow
					justifyBetween
					alignCenter
					gapSmall
				>
					<Badge
						badgeColor="lightestGray"
						badgeIcon={<Marker fill="#ff336a" />} // $pink from _variables.scss
						badgeText={
							<Paragraph
								className="distance-badge-text"
								size="tiny"
								color="black"
								weight="standard"
								last
							>
								<Text
									className="distance-value"
									size="tiny"
									color="black"
									weight="extraBold"
								>
									{`${facility?.distance ?? ''}`}
								</Text>{' '}
								miles away
							</Paragraph>
						}
					/>
					{!!facility?.reviewMetadata?.count && (
						<Badge
							badgeColor="lightestGray"
							badgeText={
								<FlexRow
									alignCenter
									gapTiny
								>
									<StarRating
										width={9}
										height={9}
										rating={facility.reviewMetadata.average}
										fillColor="#146FB4"
										outOf={5}
									/>
									<Text
										size="tiny"
										color="black"
										weight="extraBold"
										last
									>
										{facility.reviewMetadata.average?.toFixed(
											1,
										)}
									</Text>
									<Text
										className="review-count-deals"
										size="tiny"
										weight="light"
										color="black"
										last
									>
										({facility.reviewMetadata.count})
									</Text>
								</FlexRow>
							}
						/>
					)}
				</FlexRow>
				<Link
					href={`${baseUrl}${facility?.url?.facility ?? ''}`}
					hideBorder
				>
					<FlexColumn gapSmall>
						<H3
							className="facility-name-deals"
							color="black"
							itemProp="name"
							last
						>
							{facility?.name}
						</H3>
						<Paragraph
							className="facility-location"
							itemProp="address"
							itemScope
							last
							itemType="https://schema.org/PostalAddress"
							color="gray"
						>
							{[
								facility?.location?.streetAddress,
								facility?.location?.city,
								facility?.location?.state,
								facility?.location?.zip,
							]
								.filter(Boolean)
								.join(', ')}
						</Paragraph>
					</FlexColumn>
				</Link>
				<div className="facility-card-omi-deals-container">
					{hasOMI ? (
						<FlexRow
							alignCenter
							gapSmall
							className="facility-card-omi-deals"
						>
							<OnlineMoveIn className="omi-icon-deals" />
							<Text
								className="omi-text-deals"
								last
							>
								Online Move-In Available
							</Text>
						</FlexRow>
					) : (
						<div className="facility-card-omi-placeholder" />
					)}
				</div>
				<div className="facility-card-bottom">
					{lowestUnit && hasValidDisplayPrice && (
						<div className="pricing-button-deals">
							<Text className="starting-at-text">Starting at</Text>
							<Text
								className="current-price-deals"
								weight="bold"
							>
								${displayPrice}
							</Text>
							{hasDiscount && hasValidOriginalPrice && (
								<Text className="original-price-deals">
									${originalPrice}
								</Text>
							)}
						</div>
					)}
					<Button
						className="view-all-units-deals ph-button button-primary button-pink"
						href={`${baseUrl}${facility?.url?.facility ?? ''}`}
						onClick={handleTrackCTA}
					>
						<Text
							className="view-all-units-text-deals"
							strong
							color="white"
							weight="bold"
						>
							View all units
						</Text>
					</Button>
				</div>
			</FlexColumn>
			{withSchema && selfStorageSchema && (
				<script
					key="schema"
					type="application/ld+json"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
				/>
			)}
		</FlexColumn>
	);
}

DealsFacilityCard.propTypes = {
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleSelect: PropTypes.func,
	lazyLoad: PropTypes.bool,
	handleTrackCTA: PropTypes.func,
	handleDeselect: PropTypes.func,
	selfStorageSchema: PropTypes.string,
	withSchema: PropTypes.bool,
	hasOMI: PropTypes.bool,
	onVisible: PropTypes.func,
	percentVisible: PropTypes.number,
	isHighlighted: PropTypes.bool,
	isVipFacility: PropTypes.bool,
};

DealsFacilityCard.defaultProps = {
	baseUrl: '',
	lazyLoad: true,
};

export default enhance(DealsFacilityCard);
